import { TRACKED_UPLOAD_ALLOWED_IMAGE_MIMES } from './file-upload.constants.js';
/** Max size per temp file upload (20 MB). */
export const TEMP_FILE_MAX_SIZE_BYTES = 20 * 1024 * 1024;
/** Max files per upload action on the temp files page. */
export const TEMP_FILE_MAX_FILES_PER_UPLOAD = 20;
/** Parallel upload requests per batch. */
export const TEMP_FILE_UPLOAD_CONCURRENCY = 4;
export const TEMP_FILE_ALLOWED_DOCUMENT_MIMES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
];
export const TEMP_FILE_ALLOWED_ARCHIVE_MIMES = [
    'application/zip',
    'application/x-zip-compressed',
    'application/gzip',
    'application/x-gzip',
    'application/x-tar',
    'application/x-7z-compressed',
    'application/vnd.rar',
    'application/x-rar-compressed',
    'application/x-bzip2',
    'application/x-xz',
];
export const TEMP_FILE_ALLOWED_EXTENSIONS = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.txt',
    '.csv',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
    '.bmp',
    '.tif',
    '.tiff',
    '.zip',
    '.tar',
    '.gz',
    '.tgz',
    '.7z',
    '.rar',
    '.bz2',
    '.xz',
];
export const TEMP_FILE_ALLOWED_MIMES = [
    ...TRACKED_UPLOAD_ALLOWED_IMAGE_MIMES,
    ...TEMP_FILE_ALLOWED_DOCUMENT_MIMES,
    ...TEMP_FILE_ALLOWED_ARCHIVE_MIMES,
];
const extensionSet = new Set(TEMP_FILE_ALLOWED_EXTENSIONS);
const mimeSet = new Set(TEMP_FILE_ALLOWED_MIMES);
/** File input accept string for temp file manual upload. */
export const TEMP_FILE_UPLOAD_ACCEPT = [
    ...TEMP_FILE_ALLOWED_EXTENSIONS,
    ...TEMP_FILE_ALLOWED_MIMES,
].join(',');
export function tempFileUploadMaxSizeLabelMb() {
    return Math.round(TEMP_FILE_MAX_SIZE_BYTES / (1024 * 1024));
}
function fileExtension(name) {
    const lower = name.trim().toLowerCase();
    const dot = lower.lastIndexOf('.');
    if (dot <= 0 || dot === lower.length - 1)
        return '';
    return lower.slice(dot);
}
export function isTempFileUploadAllowed(file) {
    const ext = fileExtension(file.name);
    if (ext && extensionSet.has(ext)) {
        return true;
    }
    const mime = (file.type ?? '').trim().toLowerCase();
    if (mime && mimeSet.has(mime)) {
        return true;
    }
    return false;
}
function validateTempFileCandidates(files) {
    const accepted = [];
    const rejected = [];
    for (const file of files) {
        if (file.size > TEMP_FILE_MAX_SIZE_BYTES) {
            rejected.push({
                name: file.name,
                reason: `Exceeds ${tempFileUploadMaxSizeLabelMb()} MB limit`,
            });
            continue;
        }
        if (!isTempFileUploadAllowed(file)) {
            rejected.push({
                name: file.name,
                reason: 'File type not allowed',
            });
            continue;
        }
        accepted.push(file);
    }
    return { accepted, rejected };
}
/**
 * Validate a file-picker selection before upload (UI should call this first).
 * Enforces the max file count strictly — no silent trimming.
 */
export function validateTempFileUploadSelection(files) {
    if (files.length === 0) {
        return {
            accepted: [],
            rejected: [],
            selectionError: 'No files selected',
        };
    }
    if (files.length > TEMP_FILE_MAX_FILES_PER_UPLOAD) {
        return {
            accepted: [],
            rejected: [],
            selectionError: `You can upload at most ${TEMP_FILE_MAX_FILES_PER_UPLOAD} files at a time (you selected ${files.length}).`,
        };
    }
    const { accepted, rejected } = validateTempFileCandidates(files);
    if (accepted.length === 0) {
        const first = rejected[0];
        return {
            accepted: [],
            rejected,
            selectionError: `${first.name}: ${first.reason}`,
        };
    }
    return { accepted, rejected };
}
/** Client-side batch validation before uploading temp files (API safety net). */
export function validateTempFileUploadBatch(files) {
    const selection = validateTempFileUploadSelection(files);
    if (selection.selectionError) {
        return selection;
    }
    return { accepted: selection.accepted, rejected: selection.rejected };
}
//# sourceMappingURL=temp-file-upload.constants.js.map